/**
 * HAPTICS — a short buzz under the thumb on the presses that matter.
 *
 * The owner's ask is "everything that affects the comfort of the user". On a
 * phone, a key confirm (a purchase, a claim, a deploy, a surrender) that the
 * hand can FEEL lands harder than any sound, and works with the volume down.
 *
 * ── THE THREE GUARDS ───────────────────────────────────────────────────────
 *  1. FEATURE: `navigator.vibrate` must be a function. iOS Safari has none, a
 *     desktop browser usually has one that does nothing; both are fine.
 *  2. DEVICE: only when the primary pointer is COARSE. A desktop with a mouse
 *     never vibrates, even if the API exists (a laptop with a touchscreen and a
 *     mouse is a mouse device for this purpose).
 *  3. SETTING: the player's "Vibration" switch (Settings → Sound), stored on
 *     this device under `rga.ui.haptics`. Default ON; '0' turns it off.
 *
 * A call that fails any guard is a silent `false` — haptics are garnish, they
 * never throw and never queue.
 *
 * ── WHERE IT FIRES ─────────────────────────────────────────────────────────
 * The document-wide press-cue delegate (`audio/cues.ts`) calls `hapticForCue`
 * so every gold CTA, purchase, claim and refusal buzzes without a per-screen
 * edit. Inside the match (where that delegate is silent) the match calls
 * `haptic()` itself on deploy and on surrender.
 */

export type HapticKind = 'tap' | 'confirm' | 'success' | 'error' | 'heavy';

/** Vibration patterns in ms. Kept short: a long buzz reads as an alarm. */
export const HAPTIC_PATTERNS: Readonly<Record<HapticKind, number | readonly number[]>> = Object.freeze({
  tap: 8,
  confirm: 14,
  success: Object.freeze([12, 40, 18]),
  error: Object.freeze([28, 50, 28]),
  heavy: 30,
});

export const HAPTICS_STORAGE_KEY = 'rga.ui.haptics';

type Listener = (on: boolean) => void;
const listeners = new Set<Listener>();
let memo: boolean | null = null;

/** The player's switch. Default on. */
export function hapticsEnabled(): boolean {
  if (memo !== null) return memo;
  let on = true;
  try {
    on = globalThis.localStorage?.getItem(HAPTICS_STORAGE_KEY) !== '0';
  } catch {
    /* private mode: default */
  }
  memo = on;
  return on;
}

export function setHapticsEnabled(on: boolean): void {
  memo = on;
  try {
    globalThis.localStorage?.setItem(HAPTICS_STORAGE_KEY, on ? '1' : '0');
  } catch {
    /* memory only */
  }
  for (const l of listeners) l(on);
}

export function subscribeHaptics(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** True when the API exists at all — the settings switch is hidden otherwise. */
export function vibrationSupported(): boolean {
  const nav = globalThis.navigator as (Navigator & { vibrate?: unknown }) | undefined;
  return nav !== undefined && typeof nav.vibrate === 'function';
}

function coarsePointer(): boolean {
  try {
    const mm = globalThis.matchMedia;
    if (typeof mm !== 'function') return false;
    return mm.call(globalThis, '(pointer: coarse)').matches;
  } catch {
    return false;
  }
}

/** Buzz. Returns whether a vibration was actually requested. */
export function haptic(kind: HapticKind = 'tap'): boolean {
  if (!vibrationSupported() || !coarsePointer() || !hapticsEnabled()) return false;
  try {
    const pattern = HAPTIC_PATTERNS[kind];
    return (globalThis.navigator as Navigator).vibrate(
      typeof pattern === 'number' ? pattern : [...pattern],
    );
  } catch {
    return false;
  }
}

/** Which UI cues are "key confirms" that deserve a buzz. Others stay still. */
const CUE_HAPTIC: Readonly<Record<string, HapticKind>> = Object.freeze({
  confirm: 'confirm',
  purchase: 'success',
  reward: 'success',
  level_up: 'success',
  denied: 'error',
  error: 'error',
});

export function hapticForCue(cue: string): boolean {
  const kind = CUE_HAPTIC[cue];
  return kind === undefined ? false : haptic(kind);
}

/** Test seam. */
export function resetHapticsForTests(): void {
  memo = null;
  listeners.clear();
}
